import type { Scene } from "@core/scene-manager";
import type { StorageAdapter } from "@platform/storage";
import { SETTINGS_STORAGE_KEY, ONBOARDING_STORAGE_KEY, DEFAULT_SETTINGS } from "@data/settings";
import type { SettingsState } from "@data/settings";

export interface SettingsSceneDeps {
  element: HTMLElement;
  soundToggle: HTMLInputElement;
  hapticsToggle: HTMLInputElement;
  resetButton: HTMLButtonElement;
  backButton: HTMLButtonElement;
  storage: StorageAdapter;
  onBack: () => Promise<void> | void;
}

/** Сцена настроек с persist через StorageAdapter. */
export class SettingsScene implements Scene {
  readonly id = "settings" as const;
  private cleanups: (() => void)[] = [];

  constructor(private readonly deps: SettingsSceneDeps) {}

  /** Загружает настройки и подписывается на изменения. */
  onEnter(): void {
    this.deps.element.classList.add("is-visible");
    this.deps.element.setAttribute("aria-hidden", "false");

    // Загружаем настройки
    void this.loadSettings();

    // Обработчики переключателей
    const handleSound = () => {
      void this.saveSettings();
    };
    const handleHaptics = () => {
      void this.saveSettings();
    };
    const handleReset = () => {
      if (window.confirm("Reset all progress? This cannot be undone.")) {
        void this.resetProgress();
      }
    };
    const handleBack = () => {
      void this.deps.onBack();
    };

    this.deps.soundToggle.addEventListener("change", handleSound);
    this.deps.hapticsToggle.addEventListener("change", handleHaptics);
    this.deps.resetButton.addEventListener("click", handleReset);
    this.deps.backButton.addEventListener("click", handleBack);

    this.cleanups.push(
      () => this.deps.soundToggle.removeEventListener("change", handleSound),
      () => this.deps.hapticsToggle.removeEventListener("change", handleHaptics),
      () => this.deps.resetButton.removeEventListener("click", handleReset),
      () => this.deps.backButton.removeEventListener("click", handleBack),
    );
  }

  onExit(): void {
    for (const c of this.cleanups) c();
    this.cleanups = [];
    this.deps.element.classList.remove("is-visible");
    this.deps.element.setAttribute("aria-hidden", "true");
  }

  private async loadSettings(): Promise<void> {
    const saved = await this.deps.storage.get<SettingsState>(SETTINGS_STORAGE_KEY);
    const settings = saved ?? DEFAULT_SETTINGS;
    this.deps.soundToggle.checked = settings.sound;
    this.deps.hapticsToggle.checked = settings.haptics;
  }

  private async saveSettings(): Promise<void> {
    const settings: SettingsState = {
      sound: this.deps.soundToggle.checked,
      haptics: this.deps.hapticsToggle.checked,
    };
    await this.deps.storage.set<SettingsState>(SETTINGS_STORAGE_KEY, settings);
  }

  private async resetProgress(): Promise<void> {
    await this.deps.storage.remove(SETTINGS_STORAGE_KEY);
    await this.deps.storage.remove(ONBOARDING_STORAGE_KEY);
    window.location.reload();
  }
}
